// =============================================
// USER PROFILE & PROJECT TEAM TYPES
// =============================================

export type ProjectRole = 'admin' | 'editor' | 'viewer' | 'contributor'

export interface Profile {
  id: string
  email: string
  full_name?: string
  avatar_url?: string
  bio?: string
  created_at: string
  updated_at: string
}

export interface ProjectMember {
  project_id: string
  user_id: string
  role: ProjectRole
  invited_by?: string
  invited_at: string
  joined_at?: string
  created_at: string
  updated_at: string
}

export interface ProjectInvitation {
  id: string
  project_id: string
  email: string
  role: ProjectRole
  invited_by: string
  token: string
  expires_at: string
  accepted_at?: string
  created_at: string
}

export interface ProjectMemberWithProfile extends ProjectMember {
  profile?: Profile
  invited_by_profile?: Profile
}

// Input types for creating/updating
export type CreateProfileInput = {
  full_name?: string
  avatar_url?: string
  bio?: string
}

export type UpdateProfileInput = Partial<CreateProfileInput>

export type CreateProjectMemberInput = {
  project_id: string
  user_id: string
  role: ProjectRole
  invited_by?: string
}

export type UpdateProjectMemberInput = {
  role: ProjectRole
}

export type CreateProjectInvitationInput = {
  project_id: string
  email: string
  role: ProjectRole
  expires_in_days?: number
}

// Permission helpers
export const ProjectPermissions = {
  canManageMembers: (role: ProjectRole, isOwner: boolean) => isOwner || role === 'admin',
  canEdit: (role: ProjectRole) => role === 'admin' || role === 'editor',
  canView: (_role: ProjectRole) => true,
  canDelete: (role: ProjectRole, isOwner: boolean) => isOwner || role === 'admin',
}

// Role display names
export const RoleLabels: Record<ProjectRole, string> = {
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
  contributor: 'Contributor',
}

// Role descriptions
export const RoleDescriptions: Record<ProjectRole, string> = {
  admin: 'Can manage project settings, members, and edit all content',
  editor: 'Can edit project content and assign work, but cannot manage members',
  viewer: 'Can view project content, but cannot make changes',
  contributor: 'Can only view and edit items assigned to them',
}
